'use client';

import React, { useState } from 'react';

interface WishlistItem {
  id: number;
  property: string;
  location: string;
  image: string;
  imageAlt: string;
  price: string;
  rating: number;
  reviews: number;
  type: string;
  collection: string;
}

const wishlistItems: WishlistItem[] = [
  {
    id: 1,
    property: 'Gorilla Trekking Lodge',
    location: 'Bwindi, Uganda',
    image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=400&h=280&fit=crop',
    imageAlt: 'Luxury lodge nestled in Bwindi Impenetrable Forest near gorilla habitat',
    price: 'UGX 850,000/night',
    rating: 4.9,
    reviews: 124,
    type: 'Lodge',
    collection: 'Dream Trips',
  },
  {
    id: 2,
    property: 'Nile River Houseboat',
    location: 'Jinja, Uganda',
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=280&fit=crop',
    imageAlt: 'Houseboat floating on the Nile River near Jinja source',
    price: 'UGX 600,000/night',
    rating: 4.7,
    reviews: 89,
    type: 'Houseboat',
    collection: 'Dream Trips',
  },
  {
    id: 3,
    property: 'Kigali Hilltop Villa',
    location: 'Kigali, Rwanda',
    image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=400&h=280&fit=crop',
    imageAlt: 'Modern hilltop villa overlooking Kigali city skyline Rwanda',
    price: 'UGX 1,200,000/night',
    rating: 4.8,
    reviews: 56,
    type: 'Villa',
    collection: 'Weekend Escapes',
  },
  {
    id: 4,
    property: 'Zanzibar Beach Cottage',
    location: 'Zanzibar, Tanzania',
    image: 'https://images.unsplash.com/photo-1439130490301-25e322d88054?w=400&h=280&fit=crop',
    imageAlt: 'White beach cottage with turquoise ocean views in Zanzibar',
    price: 'UGX 950,000/night',
    rating: 4.9,
    reviews: 203,
    type: 'Cottage',
    collection: 'Weekend Escapes',
  },
];

const collections = ['All', 'Dream Trips', 'Weekend Escapes'];

export default function GuestWishlist() {
  const [activeCollection, setActiveCollection] = useState('All');
  const [saved, setSaved] = useState<number[]>(wishlistItems.map((i) => i.id));

  const filtered = wishlistItems.filter(
    (item) => activeCollection === 'All' || item.collection === activeCollection
  );

  const toggleSave = (id: number) => {
    setSaved((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          {collections.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCollection(c)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                activeCollection === c ? 'bg-[#4088C6] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#4088C6]/40'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <button className="flex items-center gap-2 text-sm font-semibold text-[#4088C6] hover:text-[#3070a6] transition-colors">
          <span>+</span> New Collection
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {filtered.map((item) => (
          <div key={item.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow group">
            <div className="relative h-48">
              <img src={item.image} alt={item.imageAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <button
                onClick={() => toggleSave(item.id)}
                className="absolute top-3 right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md hover:scale-110 transition-transform"
              >
                <svg className={`w-4 h-4 ${saved.includes(item.id) ? 'text-[#4088C6] fill-[#4088C6]' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
              <div className="absolute bottom-3 left-3">
                <span className="text-xs font-semibold bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full text-gray-700">{item.type}</span>
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-gray-900 text-sm mb-0.5">{item.property}</h3>
              <p className="text-xs text-gray-500 mb-2">{item.location}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <span className="text-amber-400 text-xs">★</span>
                  <span className="text-xs font-semibold text-gray-700">{item.rating}</span>
                  <span className="text-xs text-gray-400">({item.reviews})</span>
                </div>
                <span className="text-xs font-bold text-gray-900">{item.price}</span>
              </div>
              <a href="/search" className="block mt-3 w-full py-2 text-center text-xs font-semibold text-[#4088C6] border border-[#4088C6]/30 rounded-xl hover:bg-[#4088C6]/5 transition-colors">
                View Property
              </a>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16">
          <div className="text-5xl mb-4">❤️</div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">No saved properties</h3>
          <p className="text-gray-500 text-sm">Start exploring and save properties you love</p>
          <a href="/search" className="inline-block mt-4 px-6 py-2.5 bg-[#4088C6] text-white text-sm font-semibold rounded-full hover:bg-[#3070a6] transition-colors">
            Explore Properties
          </a>
        </div>
      )}
    </div>
  );
}
